import { contactColumn, dotGlyph, FlowTemplate, leadIcon, photoWidget, pill, sectionIcon, singleColumnPage, type RenderContext, type Zone } from "../base";
import { box, col, expanded, hspace, keep, linkText, padding, rect, rich, row, text, vspace, wrapBox, type Node, type PageSpec } from "../ir";
import { firstName, lastName, type RenderEntry, type RenderSection } from "../model";
import { hex, Pal, WHITE } from "../theme";

/**
 * Studio — editorial and loud: an oversized two-tone name, solid colour
 * heading tabs and date pills. For creative roles that want personality.
 * (studio_template.dart)
 */
export class StudioTemplate extends FlowTemplate {
  readonly info = {
    id: "studio",
    name: "Studio",
    tagline: "Big two-tone name and bold colour tabs",
    category: "creative" as const,
    layout: "One column",
    defaultAccent: 0xffea580c,
    atsFriendly: true,
    supportsPhoto: true,
  };

  readonly fonts = {
    body: "lato" as const,
    heading: "montserrat" as const,
    display: "montserrat" as const,
    headingFollowsBody: false,
    displayFollowsBody: false,
  };

  zoneWidth(c: RenderContext): number {
    return c.format.width - 80;
  }

  heading(c: RenderContext, s: RenderSection): Node {
    const t = c.t;
    const band = Pal.deep(t.accent);
    return row([
      box({ pad: [3.5, 8, 3, 8], bg: hex(band), radius: 3 }, [
        leadIcon(
          sectionIcon(c, s, WHITE, t.fs(9.5), true),
          text(s.title.toUpperCase(), t.style(t.heading.bold, 8.8, { color: WHITE, letterSpacing: 1.3, lineSpacing: 0 })),
          { gap: 5 }
        ),
      ]),
      hspace(8),
      expanded(rect({ height: 1.4, color: t.accentTint })),
    ]);
  }

  bulletGlyph(c: RenderContext, z: Zone): Node {
    return dotGlyph(c.t.accent, this.bulletStyle(c, z).size, 3.4, true);
  }

  entryHeader(c: RenderContext, e: RenderEntry): Node {
    const t = c.t;
    const title = e.title || e.subtitle;
    const subtitle = e.title ? e.subtitle : "";
    return col([
      row(
        [
          expanded(linkText(title, t.style(t.heading.bold, 10.4, { color: t.ink, lineSpacing: 0 }), e.url)),
          e.dates &&
            pill(e.dates, t.style(t.heading.semiBold, 7.6, { color: t.accentText, lineSpacing: 0 }), {
              background: t.accentTint,
              radius: 8,
              hPad: 6,
              vPad: 2,
            }),
        ],
        { align: "start" }
      ),
      (subtitle || e.meta) &&
        padding(
          { top: 2 },
          rich([
            subtitle ? [subtitle, t.style(t.body.bold, 9.3, { color: t.accentText })] : null,
            subtitle && e.meta ? ["  /  ", t.style(t.body.regular, 8.4, { color: t.faint })] : null,
            e.meta ? [e.meta, t.style(t.body.regular, 8.6, { color: t.muted })] : null,
          ])
        ),
    ]);
  }

  chips(c: RenderContext, items: string[]): Node {
    const t = c.t;
    return wrapBox(
      items.map((item) =>
        pill(item, t.style(t.body.bold, 8.2, { color: t.accentText, lineSpacing: 0 }), {
          background: t.accentTint,
          radius: 10,
          hPad: 8,
          vPad: 3,
        })
      ),
      { spacing: 5, runSpacing: 5 }
    );
  }

  build(c: RenderContext): PageSpec {
    const t = c.t;
    const m = c.model;
    const photo = photoWidget(c, 84, { circle: false, radius: 16 });
    const nameStyle = t.style(t.display.extraBold, 31, { color: t.ink, letterSpacing: -0.5, lineSpacing: 0 });
    const nameBlock = col([
      m.name && text(firstName(m).toUpperCase(), nameStyle),
      m.name && lastName(m) && text(lastName(m).toUpperCase(), { ...nameStyle, color: t.accent }),
      m.headline && vspace(6),
      m.headline &&
        text(m.headline.toUpperCase(), t.style(t.body.bold, 9.4, { color: t.muted, letterSpacing: 2.4, lineSpacing: 1 })),
    ]);
    const header = row([
      photo,
      photo && hspace(18),
      expanded(nameBlock),
      m.contacts.length > 0 && hspace(14),
      m.contacts.length > 0 &&
        col(
          contactColumn(c, {
            style: t.style(t.body.regular, 8.3, { color: t.text, lineSpacing: 0 }),
            iconColor: t.accent,
            gap: 4.5,
            alignRight: true,
          }),
          { width: 168, align: "end" }
        ),
    ]);
    return singleColumnPage(c, [40, 38, 40, 32], [keep([header, vspace(t.gap(22))]), ...this.allSections(c, m.sections)]);
  }
}
